import { create } from 'zustand'

import { AccountManager } from '@/stores/shared/account-manager'
import { migrateTokenToMultiAccount, StoredAccount } from '@/utils/auth'

// Global sync channel singleton to prevent multiple listeners
let globalSyncChannel: BroadcastChannel | null = null
let lastSwitchTimestamp = 0

// Setup cross-tab account synchronization with cleanup
const setupCrossTabSync = () => {
  // Prevent multiple listeners
  if (globalSyncChannel) {
    return globalSyncChannel
  }

  if ('BroadcastChannel' in window) {
    globalSyncChannel = new BroadcastChannel('glows-account-sync')

    const handleMessage = (event: MessageEvent) => {
      const { type, timestamp } = event.data

      // Prevent reload loops - only handle if this is a newer event
      if (timestamp <= lastSwitchTimestamp) {
        return
      }

      lastSwitchTimestamp = timestamp

      switch (type) {
        case 'ACCOUNT_SWITCHED':
          window.location.reload()
          break

        case 'ACCOUNT_REMOVED':
          window.location.reload()
          break

        case 'ACCOUNT_ADDED':
          window.location.reload()
          break

        case 'ACCOUNTS_CLEARED':
          window.location.reload()
          break

        default:
          console.error('Unknown cross-tab event type:', type)
      }
    }

    globalSyncChannel.addEventListener('message', handleMessage)

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      if (globalSyncChannel) {
        globalSyncChannel.close()
        globalSyncChannel = null
      }
    })

    return globalSyncChannel
  }
  return null
}

// Initialize cross-tab sync
setupCrossTabSync()

interface AccountsStoreState {
  accounts: StoredAccount[]
  activeAccountId: number | null
  isLoaded: boolean

  initialize: (userInfo?: { id: number; name: string; email: string }) => Promise<void>
  switchAccount: (accountId: number) => Promise<void>
  addNewAccount: (account: StoredAccount) => Promise<void>
  removeAccountById: (accountId: number) => Promise<void>
  clearAll: () => void
  getActiveAccount: () => StoredAccount | null
}

export const useAccountsStore = create<AccountsStoreState>((set, get) => {
  // Register this store instance with AccountManager
  const storeApi = {
    getState: get,
    setState: set,
  }
  AccountManager.registerAccountsStore(storeApi)

  return {
    accounts: [],
    activeAccountId: null,
    isLoaded: false,

    async initialize(userInfo?: { id: number; name: string; email: string }) {
      try {
        // Prevent multiple initializations
        const currentState = get()
        if (currentState.isLoaded) {
          return
        }

        let accounts = AccountManager.getStoredAccounts()
        let activeAccountId = AccountManager.getActiveAccountId()

        // Only attempt migration if we have valid user info and conditions are met
        if (userInfo && userInfo.id > 0 && migrateTokenToMultiAccount(userInfo)) {
          // Refresh account data after migration
          accounts = AccountManager.getStoredAccounts()
          activeAccountId = AccountManager.getActiveAccountId()
        }

        set({
          accounts,
          activeAccountId,
          isLoaded: true,
        })
      } catch (error) {
        console.error('Failed to initialize accounts store:', error)
        set({
          accounts: [],
          activeAccountId: null,
          isLoaded: true,
        })
      }
    },

    async switchAccount(accountId: number) {
      try {
        await AccountManager.switchAccount(accountId)
      } catch (error) {
        console.error('Failed to switch account:', error)
        throw error
      }
    },

    async addNewAccount(account: StoredAccount) {
      try {
        await AccountManager.saveAccount(account)
      } catch (error) {
        console.error('Failed to add account:', error)
        throw error
      }
    },

    async removeAccountById(accountId: number) {
      try {
        await AccountManager.removeAccountById(accountId)
      } catch (error) {
        console.error('Failed to remove account:', error)
        throw error
      }
    },

    clearAll() {
      try {
        AccountManager.clearAll()
      } catch (error) {
        console.error('Failed to clear all accounts:', error)
        throw error
      }
    },

    getActiveAccount() {
      return AccountManager.getActiveAccount()
    },
  }
})

export const useActiveAccountId = () => useAccountsStore((state) => state.activeAccountId)
export const useStoredAccounts = () => useAccountsStore((state) => state.accounts)
export const useAccountsLoaded = () => useAccountsStore((state) => state.isLoaded)
